import { useAtomValue } from 'jotai';

import { DirectMessageService } from '@/components/pages/conversations/SocialInbox/services/DirectMessageService';

import { useSocialInboxDataSource } from '@/components/pages/conversations/SocialInbox/hooks/useSocialInboxDataSource';
import useAwsTranslate from '@/utils/hooks/language/useAwsTranslate';
import { useDMConversationMessages } from '@/utils/hooks/swr/conversations/useDMConversationMessages';
import useTranslation from '@/utils/hooks/translation/useTranslation';
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';

import { classNames } from '@/utils';

import type {
    DMConversation,
    DMConversationMessage
} from '@/components/pages/conversations/SocialInbox/types';

import { DM_STATUS } from '@/components/pages/conversations/SocialInbox/const';

import {
    conversationParticipantProfilePicturesAtom,
    openedMessageAtom
} from '@/components/pages/conversations/SocialInbox/atoms';

import { DefaultAvatar } from '@/components/base/Avatar';
import Icon from '@/components/base/Icon';
import Loader from '@/components/base/Loader';
import { Trans } from '@/components/tools/translation';
import { MessageDate } from '@/components/pages/conversations/SocialInbox/components/MessageDate';
import { SocialInboxMessageActions } from '@/components/pages/conversations/SocialInbox/components/SocialInboxChatView/SocialInboxMessageActions/SocialInboxMessageActions';
import { DirectMessageBubbleContent } from '@/components/pages/conversations/SocialInbox/components/SocialInboxChatView/SocialInboxMessages/SocialInboxDirectMessages/DirectMessageBubble/DirectMessageBubbleContent';
import { MessageMedia } from '@/components/pages/conversations/SocialInbox/components/SocialInboxChatView/SocialInboxMessages/SocialInboxDirectMessages/MessageMedia';
import { UnsupportedMessage } from '@/components/pages/conversations/SocialInbox/components/SocialInboxChatView/SocialInboxMessages/SocialInboxDirectMessages/UnsupportedMessage';

interface DirectMessageBubbleProps {
    className?: string;
    isGroupedWithPrevious?: boolean;
    isReply?: boolean;
    message: DMConversationMessage;
}

export const DirectMessageBubble = ({
    className,
    isGroupedWithPrevious = false,
    isReply = false,
    message
}: DirectMessageBubbleProps) => {
    const { t } = useTranslation('conversations');
    const { awsTranslation, awsToggleTranslation } = useAwsTranslate(message?.content);

    const openedConversation = useAtomValue(openedMessageAtom) as DMConversation;
    const conversationParticipantProfilePictures = useAtomValue(
        conversationParticipantProfilePicturesAtom
    );

    const { activeSocialProfile } = useActiveSocialProfile();
    const { mutateMessages: mutateConversations } = useSocialInboxDataSource();
    const { mutate: mutateMessages } = useDMConversationMessages({
        activeSPID: activeSocialProfile?.id,
        conversationId: message.meta_conversation_id
    });

    const isSent = !!message?.is_from_me;

    const updateMessageStatus = (message: DMConversationMessage) => {
        mutateConversations(
            (conversations: DMConversation[]) =>
                DirectMessageService.hydrateConversations(conversations, message, true),
            { revalidate: false }
        );
        mutateMessages((data) => DirectMessageService.hydrateMessages(data, message), {
            revalidate: false
        });
    };

    const getProfilePicture = () => {
        if (isSent) {
            return activeSocialProfile?.external_api_data?.avatar;
        }

        return conversationParticipantProfilePictures[
            openedConversation?.initiator.meta_participant_id
        ];
    };

    const renderAvatar = () => {
        if (isGroupedWithPrevious) {
            return <div className="h-8 w-8 shrink-0" />;
        }

        if (!isSent) {
            const profileLink = DirectMessageService.getProfileLink(
                openedConversation.initiator.name,
                activeSocialProfile?.social_network_type
            );
            const avatar = (
                <DefaultAvatar
                    size="sm"
                    url={getProfilePicture()}
                    fallback={DirectMessageService.getUserInitials(
                        openedConversation?.initiator.name
                    )}
                />
            );

            if (profileLink) {
                return (
                    <a href={profileLink} target="_blank" rel="noopener noreferrer">
                        {avatar}
                    </a>
                );
            }

            return avatar;
        }

        return (
            <DefaultAvatar
                size="sm"
                url={getProfilePicture()}
                fallback={DirectMessageService.getUserInitials(openedConversation?.recipient.name)}
            />
        );
    };

    const renderInitiatorName = () => {
        if (isSent || isGroupedWithPrevious) return null;

        const profileLink = DirectMessageService.getProfileLink(
            openedConversation.initiator.name,
            activeSocialProfile?.social_network_type
        );

        if (profileLink) {
            return (
                <a
                    href={profileLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classNames('font-GSemiBold text-sm', {
                        'hover:text-icoBlue': profileLink
                    })}>
                    {openedConversation.initiator.name}
                </a>
            );
        }

        return <span className="font-GSemiBold text-sm">{openedConversation.initiator.name}</span>;
    };

    const renderMessageContent = () => {
        if (!message.content && message.attachments?.length) return null;

        if (!message.content && !message.attachments?.length) {
            return <UnsupportedMessage isSent={isSent} />;
        }

        return (
            <div
                className={`relative flex max-w-full flex-col space-y-1 rounded-2xl p-4 ${
                    isSent ? 'bg-icoBlue text-white' : 'bg-icoGray-100 text-icoBlack'
                }`}>
                {renderInitiatorName()}

                <DirectMessageBubbleContent awsTranslation={awsTranslation} message={message} />

                {!isReply && (
                    <SocialInboxMessageActions
                        awsTranslation={awsTranslation}
                        toggleAwsTranslation={awsToggleTranslation}
                        className={classNames(
                            'absolute top-1/2 -translate-y-1/2 opacity-0 transition-opacity duration-200 group-hover:opacity-100',
                            isSent ? '-left-2 -translate-x-full' : '-right-2 translate-x-full'
                        )}
                    />
                )}
            </div>
        );
    };

    return (
        <div
            className={classNames(
                'relative flex w-full flex-col',
                isSent ? 'ml-auto gap-2' : 'mr-auto',
                isReply ? 'pr-11' : 'group max-w-[80%]',
                className
            )}>
            <div
                className={classNames(
                    'relative flex items-end gap-2',
                    isSent ? 'flex-row-reverse' : 'flex-row',
                    isReply && 'border-r-2 border-icoGray-300 pr-4'
                )}>
                {renderAvatar()}

                <div className="relative flex min-w-24 flex-col gap-2">
                    {!isGroupedWithPrevious && (
                        <div
                            className={`flex w-full items-center gap-1 ${
                                isSent || isReply ? 'justify-end' : ''
                            }`}>
                            {message.status === DM_STATUS.SENDING ? (
                                <div className="flex items-center gap-1">
                                    <Loader className="h-3 w-3 text-icoBlue" />
                                    <span className="text-xs text-icoBlue">
                                        {t('direct_messages.sending')}
                                    </span>
                                </div>
                            ) : (
                                <MessageDate date={message.sent_at} />
                            )}
                        </div>
                    )}
                    {isReply && (
                        <div className="flex justify-end gap-1">
                            <span className="font-GMedium text-xs">
                                {t('direct_messages.answered_to', {
                                    sender: openedConversation.initiator.name
                                })}
                            </span>
                        </div>
                    )}

                    {message.attachments?.map((attachment) => (
                        <MessageMedia
                            key={attachment.url}
                            attachment={attachment}
                            isSent={isSent}
                        />
                    ))}

                    {renderMessageContent()}
                </div>
            </div>
            {isSent && message?.status === DM_STATUS.FAILED && (
                <div className="flex items-center justify-end gap-1 font-GMedium text-xs text-icoRed-400">
                    <Icon icon="warning" />
                    <Trans
                        i18nKey="conversations:direct_messages.message_not_sent"
                        components={{
                            link: (
                                <button
                                    onClick={() => {
                                        DirectMessageService.sendMessage(
                                            message.content,
                                            activeSocialProfile,
                                            openedConversation.meta_conversation_id,
                                            updateMessageStatus,
                                            message.tempId
                                        );
                                    }}
                                    className="cursor-pointer border-0 bg-transparent p-0 text-current underline"
                                    type="button"
                                />
                            )
                        }}
                    />
                </div>
            )}
        </div>
    );
};
